import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import {
  Plus, ChevronRight, ChevronDown, Check, Lock, BookOpen,
  Play, Edit3, Trash2, X, Save, Clock, Link2, FileText,
  Lightbulb, Zap, Trophy, ArrowLeft, MoreHorizontal,
  GripVertical, Circle,
} from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { id: "general",    label: "General",      color: "#5A90F5" },
  { id: "technical",  label: "Technical",    color: "#A855F7" },
  { id: "onboarding", label: "Onboarding",   color: "#00C781" },
  { id: "compliance", label: "Compliance",   color: "#F59E0B" },
  { id: "personal",   label: "Personal",     color: "#EC4899" },
];

const LESSON_TYPES = [
  { id: "read",     label: "Read",     icon: FileText, color: "#5A90F5" },
  { id: "watch",    label: "Watch",    icon: Play,     color: "#EF4444" },
  { id: "practice", label: "Practice", icon: Zap,      color: "#FF8A00" },
  { id: "reflect",  label: "Reflect",  icon: Lightbulb,color: "#A855F7" },
];

const COLORS = ["#5A90F5","#A855F7","#00C781","#F59E0B","#EF4444","#EC4899","#14B8A6","#FF8A00"];

function typeInfo(type) { return LESSON_TYPES.find(t => t.id === type) || LESSON_TYPES[0]; }
function catInfo(cat) { return CATEGORIES.find(c => c.id === cat) || CATEGORIES[0]; }

// ─── Course Form Modal ────────────────────────────────────────────────────────
function CourseModal({ initial, onClose, onSave }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [cat, setCat] = useState(initial?.category || "general");
  const [emoji, setEmoji] = useState(initial?.emoji || "📚");
  const [color, setColor] = useState(initial?.color || "#5A90F5");

  return (
    <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141414] border border-[#2A2A2A] rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-[#EDEDED] mb-5">{initial ? "Edit" : "New"} course</h3>
        <div className="flex gap-3 mb-3">
          <input value={emoji} onChange={e => setEmoji(e.target.value)} placeholder="📚"
            className="w-14 text-center bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl text-2xl outline-none focus:border-[#5A90F5]" />
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Course title…" autoFocus
            className="flex-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[13px] text-[#EDEDED] placeholder-[#52525B] outline-none focus:border-[#5A90F5]" />
        </div>
        <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="What will learners get from this course?"
          rows={2} className="w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl px-3 py-2.5 text-[12px] text-[#EDEDED] placeholder-[#52525B] outline-none resize-none mb-3" />
        <div className="flex flex-wrap gap-1.5 mb-3">
          {CATEGORIES.map(c => (
            <button key={c.id} onClick={() => { setCat(c.id); setColor(c.color); }}
              className="px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all"
              style={{ borderColor: cat === c.id ? c.color : "#2A2A2A", background: cat === c.id ? c.color + "25" : "transparent", color: cat === c.id ? c.color : "#71717A" }}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5 mb-5">
          {COLORS.map(c => (
            <button key={c} onClick={() => setColor(c)} className="w-6 h-6 rounded-full border-2 transition-all"
              style={{ background: c, borderColor: color === c ? "#fff" : "transparent" }} />
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-[12px] border border-[#2A2A2A] text-[#71717A]">Cancel</button>
          <button onClick={() => title.trim() && onSave({ title: title.trim(), description: desc, category: cat, emoji, color })} disabled={!title.trim()}
            className="flex-1 py-2.5 rounded-xl text-[12px] font-semibold text-white disabled:opacity-30 transition-colors"
            style={{ background: color }}>
            {initial ? "Save" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Lesson Viewer / Editor ───────────────────────────────────────────────────
function LessonView({ lesson, course, isLocked, canEdit, onComplete, onUncomplete, onUpdate, onClose }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(lesson.title);
  const [content, setContent] = useState(lesson.content || "");
  const [url, setUrl] = useState(lesson.url || "");
  const [notes, setNotes] = useState(lesson.notes || "");
  const [saving, setSaving] = useState(false);
  const info = typeInfo(lesson.type);
  const Icon = info.icon;
  const isDone = !!lesson.completed_at;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await onUpdate(lesson.lesson_id, { title: title.trim(), content, url: url || null, notes });
      setEditing(false);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const formatContent = (text) => text.split("\n").map((line, i) => {
    if (line.startsWith("# ")) return <h2 key={i} className="text-[16px] font-bold text-[#EDEDED] mt-4 mb-2">{line.slice(2)}</h2>;
    if (line.startsWith("## ")) return <h3 key={i} className="text-[13px] font-semibold text-[#EDEDED] mt-3 mb-1">{line.slice(3)}</h3>;
    if (line.startsWith("- ")) return <p key={i} className="text-[13px] text-[#A1A1AA] mb-1 pl-4">• {line.slice(2)}</p>;
    if (line.startsWith("**") && line.endsWith("**")) return <p key={i} className="text-[13px] font-semibold text-[#EDEDED] mb-1">{line.slice(2,-2)}</p>;
    if (line.trim() === "") return <div key={i} className="h-2" />;
    return <p key={i} className="text-[13px] text-[#A1A1AA] leading-relaxed mb-1">{line}</p>;
  });

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      {/* Header */}
      <div className="flex items-center gap-3 px-8 py-4 border-b border-[#1A1A1A] flex-shrink-0">
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#141414] text-[#71717A] hover:text-[#A1A1AA]">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 flex-1">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: info.color + "25" }}>
            <Icon className="w-3.5 h-3.5" style={{ color: info.color }} />
          </div>
          <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: info.color }}>{info.label}</span>
          {lesson.duration_minutes && (
            <span className="text-[10px] text-[#52525B] flex items-center gap-1 ml-2"><Clock className="w-3 h-3" />{lesson.duration_minutes}m</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && !editing && <button onClick={() => setEditing(true)} className="p-1.5 rounded-lg hover:bg-[#141414] text-[#71717A] hover:text-[#A1A1AA]"><Edit3 className="w-3.5 h-3.5" /></button>}
          {editing && (
            <>
              <button onClick={() => { setEditing(false); setTitle(lesson.title); setContent(lesson.content||""); setUrl(lesson.url||""); }} className="px-3 py-1.5 rounded-xl text-[11px] border border-[#2A2A2A] text-[#71717A]">Cancel</button>
              <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-semibold text-white bg-[#5A90F5] hover:bg-[#4A80E5] disabled:opacity-50">
                <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-8 py-8">
          {isLocked ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#111] border border-[#2A2A2A] flex items-center justify-center">
                <Lock className="w-7 h-7 text-[#52525B]" />
              </div>
              <p className="text-[15px] font-semibold text-[#52525B]">Lesson locked</p>
              <p className="text-[12px] text-[#3A3A3A]">Complete the previous lesson to unlock this one</p>
            </div>
          ) : (
            <>
              {/* Title */}
              {editing ? (
                <input value={title} onChange={e => setTitle(e.target.value)}
                  className="w-full bg-transparent text-[26px] font-bold text-[#EDEDED] outline-none mb-3 border-b border-[#2A2A2A] pb-2" />
              ) : (
                <h1 className="text-[26px] font-bold text-[#EDEDED] mb-6">{lesson.title}</h1>
              )}

              {/* URL for watch type */}
              {(lesson.type === "watch" || editing) && (
                <div className="mb-6">
                  {editing ? (
                    <div className="flex items-center gap-2 p-3 bg-[#111] border border-[#2A2A2A] rounded-xl">
                      <Link2 className="w-3.5 h-3.5 text-[#52525B]" />
                      <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://youtube.com/watch?v=…"
                        className="flex-1 bg-transparent text-[12px] text-[#5A90F5] placeholder-[#3A3A3A] outline-none" />
                    </div>
                  ) : url ? (
                    <a href={url} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 p-3 bg-[#111] border border-[#2A2A2A] rounded-xl hover:border-[#EF4444] transition-colors group">
                      <Play className="w-4 h-4 text-[#EF4444]" />
                      <span className="text-[12px] text-[#5A90F5] truncate flex-1">{url}</span>
                    </a>
                  ) : null}
                </div>
              )}

              {/* Main content */}
              <div className="mb-8">
                {editing ? (
                  <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Write the lesson content here…&#10;&#10;Use # for headings, - for bullet points, **text** for bold" rows={16}
                    className="w-full bg-[#111] border border-[#2A2A2A] rounded-xl px-4 py-3 text-[13px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none resize-none leading-relaxed font-mono focus:border-[#5A90F5] transition-colors" />
                ) : (
                  <div>{formatContent(content || "_No content yet._")}</div>
                )}
              </div>

              {/* Notes */}
              {!editing && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#52525B] mb-2">Your notes</p>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)}
                    onBlur={() => onUpdate(lesson.lesson_id, { notes })}
                    placeholder="Write your notes here…"
                    rows={4}
                    className="w-full bg-[#111] border border-[#1A1A1A] rounded-xl px-4 py-3 text-[12px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none resize-none leading-relaxed focus:border-[#2A2A2A] transition-colors" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Complete button */}
      {!isLocked && !editing && (
        <div className="flex-shrink-0 px-8 py-4 border-t border-[#1A1A1A]">
          {isDone ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[#00C781]">
                <Check className="w-4 h-4" />
                <span className="text-[12px] font-medium">Completed {new Date(lesson.completed_at).toLocaleDateString([], { month: "short", day: "numeric" })}</span>
              </div>
              <button onClick={onUncomplete} className="text-[11px] text-[#52525B] hover:text-[#A1A1AA] transition-colors">Mark incomplete</button>
            </div>
          ) : (
            <button onClick={onComplete}
              className="w-full py-3 rounded-xl text-[13px] font-semibold text-white transition-colors flex items-center justify-center gap-2"
              style={{ background: course.color || "#5A90F5" }}>
              <Check className="w-4 h-4" /> Mark complete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Module / Lesson Row ──────────────────────────────────────────────────────
function LessonRow({ lesson, index, isLocked, isActive, onClick, canEdit, onDelete }) {
  const info = typeInfo(lesson.type);
  const Icon = info.icon;
  const isDone = !!lesson.completed_at;

  return (
    <button onClick={() => !isLocked && onClick(lesson)}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all group ${isActive ? "bg-[#141414] border border-[#2A2A2A]" : isLocked ? "opacity-50 cursor-not-allowed" : "hover:bg-[#0F0F0F]"}`}>
      {/* Status indicator */}
      <div className="flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center"
        style={{ borderColor: isDone ? "#00C781" : isLocked ? "#2A2A2A" : info.color, background: isDone ? "#00C781" : "transparent" }}>
        {isDone ? <Check className="w-3 h-3 text-black" /> : isLocked ? <Lock className="w-2.5 h-2.5 text-[#3A3A3A]" /> : <span className="text-[9px] font-bold" style={{ color: info.color }}>{index + 1}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-[12px] font-medium truncate ${isDone ? "line-through text-[#52525B]" : isLocked ? "text-[#52525B]" : "text-[#EDEDED]"}`}>{lesson.title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <Icon className="w-2.5 h-2.5 flex-shrink-0" style={{ color: isLocked ? "#3A3A3A" : info.color }} />
          <span className="text-[9px]" style={{ color: isLocked ? "#3A3A3A" : info.color }}>{info.label}</span>
          {lesson.duration_minutes && <span className="text-[9px] text-[#52525B]">· {lesson.duration_minutes}m</span>}
        </div>
      </div>
      {canEdit && !isLocked && (
        <button onClick={e => { e.stopPropagation(); onDelete(lesson.lesson_id); }}
          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#EF4444]/10 text-[#3A3A3A] hover:text-[#EF4444] transition-all">
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </button>
  );
}

// ─── Add Lesson Form ──────────────────────────────────────────────────────────
function AddLessonForm({ moduleId, onSave, onCancel }) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("read");
  const [duration, setDuration] = useState("");

  return (
    <div className="px-4 py-3 bg-[#0F0F0F] rounded-xl border border-[#2A2A2A] mt-2">
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Lesson title…" autoFocus
        className="w-full bg-transparent text-[12px] text-[#EDEDED] placeholder-[#52525B] outline-none mb-3" />
      <div className="flex gap-1.5 mb-3">
        {LESSON_TYPES.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setType(t.id)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] border transition-all"
              style={{ borderColor: type === t.id ? t.color : "#2A2A2A", background: type === t.id ? t.color + "20" : "transparent", color: type === t.id ? t.color : "#52525B" }}>
              <Icon className="w-2.5 h-2.5" /> {t.label}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-3 h-3 text-[#52525B]" />
        <input value={duration} onChange={e => setDuration(e.target.value)} type="number" placeholder="Duration (mins)"
          className="flex-1 bg-transparent text-[11px] text-[#EDEDED] placeholder-[#52525B] outline-none" />
      </div>
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 py-1.5 rounded-lg text-[11px] border border-[#2A2A2A] text-[#71717A]">Cancel</button>
        <button onClick={() => title.trim() && onSave({ title: title.trim(), type, duration_minutes: duration ? parseInt(duration) : null, module_id: moduleId })}
          disabled={!title.trim()} className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold text-white bg-[#5A90F5] hover:bg-[#4A80E5] disabled:opacity-30">
          Add lesson
        </button>
      </div>
    </div>
  );
}

// ─── Course Detail View ───────────────────────────────────────────────────────
function CourseDetail({ course, wsId, onBack, onCourseUpdate }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLesson, setActiveLesson] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [addingModuleTitle, setAddingModuleTitle] = useState("");
  const [showAddModule, setShowAddModule] = useState(false);
  const [addingLesson, setAddingLesson] = useState(null); // moduleId
  const [canEdit] = useState(true);

  const load = useCallback(async () => {
    if (!wsId || !course) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/courses/${course.course_id}/modules`);
      setModules(data || []);
      // Auto-expand all by default
      const exp = {};
      (data || []).forEach(m => { exp[m.module_id] = true; });
      setExpandedModules(exp);
    } catch {} finally { setLoading(false); }
  }, [wsId, course]);

  useEffect(() => { load(); }, [load]);

  // Flatten all lessons in order for locking logic
  const allLessons = modules.flatMap(m => m.lessons || []);
  const isLocked = (lesson) => {
    const idx = allLessons.findIndex(l => l.lesson_id === lesson.lesson_id);
    if (idx === 0) return false;
    const prev = allLessons[idx - 1];
    return !prev?.completed_at;
  };

  const totalLessons = allLessons.length;
  const doneLessons = allLessons.filter(l => l.completed_at).length;
  const pct = totalLessons > 0 ? Math.round(doneLessons / totalLessons * 100) : 0;

  const addModule = async () => {
    if (!addingModuleTitle.trim()) return;
    try {
      const { data } = await api.post(`/workspaces/${wsId}/courses/${course.course_id}/modules`, { title: addingModuleTitle.trim() });
      setModules(m => [...m, { ...data, lessons: [] }]);
      setExpandedModules(e => ({ ...e, [data.module_id]: true }));
      setAddingModuleTitle(""); setShowAddModule(false);
    } catch { toast.error("Failed to add module"); }
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm("Delete this module and all its lessons?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/courses/${course.course_id}/modules/${moduleId}`);
      setModules(m => m.filter(x => x.module_id !== moduleId));
    } catch {}
  };

  const addLesson = async (body) => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/courses/${course.course_id}/lessons`, body);
      setModules(m => m.map(mod => mod.module_id === body.module_id ? { ...mod, lessons: [...(mod.lessons || []), data], total: (mod.total || 0) + 1 } : mod));
      setAddingLesson(null);
    } catch { toast.error("Failed to add lesson"); }
  };

  const deleteLesson = async (lessonId) => {
    try {
      await api.delete(`/workspaces/${wsId}/courses/${course.course_id}/lessons/${lessonId}`);
      setModules(m => m.map(mod => ({ ...mod, lessons: (mod.lessons || []).filter(l => l.lesson_id !== lessonId) })));
      if (activeLesson?.lesson_id === lessonId) setActiveLesson(null);
    } catch {}
  };

  const updateLesson = async (lessonId, patch) => {
    const { data } = await api.put(`/workspaces/${wsId}/courses/${course.course_id}/lessons/${lessonId}`, patch);
    setModules(m => m.map(mod => ({ ...mod, lessons: (mod.lessons || []).map(l => l.lesson_id === lessonId ? data : l) })));
    if (activeLesson?.lesson_id === lessonId) setActiveLesson(data);
    return data;
  };

  const completeLesson = async (lessonId) => {
    const { data } = await api.post(`/workspaces/${wsId}/courses/${course.course_id}/lessons/${lessonId}/complete`);
    setModules(m => m.map(mod => ({ ...mod, lessons: (mod.lessons || []).map(l => l.lesson_id === lessonId ? data : l) })));
    if (activeLesson?.lesson_id === lessonId) setActiveLesson(data);
    toast.success("Lesson complete! 🎉");
    onCourseUpdate();
    // Auto-advance to next lesson
    const idx = allLessons.findIndex(l => l.lesson_id === lessonId);
    if (idx >= 0 && idx < allLessons.length - 1) {
      setTimeout(() => setActiveLesson({ ...allLessons[idx + 1] }), 600);
    }
  };

  const uncompleteLesson = async (lessonId) => {
    const { data } = await api.post(`/workspaces/${wsId}/courses/${course.course_id}/lessons/${lessonId}/uncomplete`);
    setModules(m => m.map(mod => ({ ...mod, lessons: (mod.lessons || []).map(l => l.lesson_id === lessonId ? data : l) })));
    if (activeLesson?.lesson_id === lessonId) setActiveLesson(data);
    onCourseUpdate();
  };

  if (activeLesson) {
    const fresh = allLessons.find(l => l.lesson_id === activeLesson.lesson_id) || activeLesson;
    return (
      <LessonView
        lesson={fresh}
        course={course}
        isLocked={isLocked(fresh)}
        canEdit={canEdit}
        onComplete={() => completeLesson(fresh.lesson_id)}
        onUncomplete={() => uncompleteLesson(fresh.lesson_id)}
        onUpdate={updateLesson}
        onClose={() => setActiveLesson(null)}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Course header */}
      <div className="px-8 py-6 border-b border-[#1A1A1A] flex-shrink-0" style={{ background: course.color + "10" }}>
        <button onClick={onBack} className="flex items-center gap-1 text-[10px] text-[#71717A] hover:text-[#A1A1AA] mb-4 transition-colors">
          <ArrowLeft className="w-3 h-3" /> All courses
        </button>
        <div className="flex items-start gap-4">
          <span className="text-4xl">{course.emoji}</span>
          <div className="flex-1 min-w-0">
            <h1 className="text-[20px] font-bold text-[#EDEDED]">{course.title}</h1>
            {course.description && <p className="text-[12px] text-[#71717A] mt-1">{course.description}</p>}
            <div className="flex items-center gap-3 mt-3">
              <div className="flex-1 max-w-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-[#71717A]">{doneLessons} / {totalLessons} lessons</span>
                  <span className="text-[10px] font-semibold" style={{ color: course.color }}>{pct}%</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden bg-[#1A1A1A]">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: course.color }} />
                </div>
              </div>
              {pct === 100 && (
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold" style={{ background: "#00C78120", color: "#00C781" }}>
                  <Trophy className="w-3 h-3" /> Complete!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modules + lessons */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading ? (
          <div className="text-[12px] text-[#52525B] py-8 text-center">Loading…</div>
        ) : (
          <div className="max-w-2xl mx-auto space-y-3">
            {modules.map((mod, modIdx) => (
              <div key={mod.module_id} className="bg-[#111] border border-[#1A1A1A] rounded-2xl overflow-hidden">
                {/* Module header */}
                <div className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-[#0F0F0F] transition-colors group"
                  onClick={() => setExpandedModules(e => ({ ...e, [mod.module_id]: !e[mod.module_id] }))}>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${mod.done === mod.total && mod.total > 0 ? "border-[#00C781] bg-[#00C781]" : "border-[#2A2A2A]"}`}>
                    {mod.done === mod.total && mod.total > 0 ? <Check className="w-2.5 h-2.5 text-black" /> : <span className="text-[9px] text-[#52525B]">{modIdx + 1}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-[#EDEDED]">{mod.title}</p>
                    <p className="text-[10px] text-[#52525B]">{mod.done}/{mod.total} lessons</p>
                  </div>
                  {canEdit && (
                    <button onClick={e => { e.stopPropagation(); deleteModule(mod.module_id); }}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#EF4444]/10 text-[#3A3A3A] hover:text-[#EF4444] transition-all">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                  {expandedModules[mod.module_id] ? <ChevronDown className="w-3.5 h-3.5 text-[#52525B] flex-shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-[#52525B] flex-shrink-0" />}
                </div>

                {/* Lessons */}
                {expandedModules[mod.module_id] && (
                  <div className="px-3 pb-3 space-y-1 border-t border-[#1A1A1A]">
                    {(mod.lessons || []).map((lesson, lessonIdx) => (
                      <LessonRow
                        key={lesson.lesson_id}
                        lesson={lesson}
                        index={allLessons.findIndex(l => l.lesson_id === lesson.lesson_id)}
                        isLocked={isLocked(lesson)}
                        isActive={activeLesson?.lesson_id === lesson.lesson_id}
                        onClick={setActiveLesson}
                        canEdit={canEdit}
                        onDelete={deleteLesson}
                      />
                    ))}
                    {addingLesson === mod.module_id ? (
                      <AddLessonForm moduleId={mod.module_id} onSave={addLesson} onCancel={() => setAddingLesson(null)} />
                    ) : canEdit && (
                      <button onClick={() => setAddingLesson(mod.module_id)}
                        className="w-full flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] text-[#52525B] hover:text-[#A1A1AA] hover:bg-[#0F0F0F] transition-colors">
                        <Plus className="w-3 h-3" /> Add lesson
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Add module */}
            {canEdit && (
              showAddModule ? (
                <div className="bg-[#111] border border-[#2A2A2A] rounded-2xl p-4">
                  <input value={addingModuleTitle} onChange={e => setAddingModuleTitle(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") addModule(); if (e.key === "Escape") setShowAddModule(false); }}
                    placeholder="Module title…" autoFocus
                    className="w-full bg-transparent text-[13px] text-[#EDEDED] placeholder-[#52525B] outline-none mb-3" />
                  <div className="flex gap-2">
                    <button onClick={() => setShowAddModule(false)} className="flex-1 py-1.5 rounded-xl text-[11px] border border-[#2A2A2A] text-[#71717A]">Cancel</button>
                    <button onClick={addModule} disabled={!addingModuleTitle.trim()} className="flex-1 py-1.5 rounded-xl text-[11px] font-semibold text-white bg-[#5A90F5] hover:bg-[#4A80E5] disabled:opacity-30">Add module</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowAddModule(true)}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-dashed border-[#2A2A2A] text-[12px] text-[#52525B] hover:text-[#A1A1AA] hover:border-[#3A3A3A] transition-all">
                  <Plus className="w-3.5 h-3.5" /> Add module
                </button>
              )
            )}

            {modules.length === 0 && !showAddModule && (
              <div className="text-center py-12">
                <p className="text-[14px] font-semibold text-[#52525B]">No modules yet</p>
                <p className="text-[11px] text-[#3A3A3A] mt-1">Add a module to start building your course</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Course Card ──────────────────────────────────────────────────────────────
function CourseCard({ course, onOpen, onEdit, onDelete }) {
  const cat = catInfo(course.category);
  const pct = course.total_lessons > 0 ? Math.round(course.completed_lessons / course.total_lessons * 100) : 0;

  return (
    <div className="group bg-[#111] border border-[#1A1A1A] rounded-2xl p-5 hover:border-[#2A2A2A] transition-all cursor-pointer flex flex-col gap-3"
      onClick={() => onOpen(course)}>
      <div className="flex items-start justify-between">
        <span className="text-3xl">{course.emoji}</span>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
          <button onClick={e => { e.stopPropagation(); onEdit(course); }} className="p-1.5 rounded-lg hover:bg-[#1A1A1A] text-[#52525B] hover:text-[#A1A1AA]"><Edit3 className="w-3 h-3" /></button>
          <button onClick={e => { e.stopPropagation(); onDelete(course.course_id); }} className="p-1.5 rounded-lg hover:bg-[#EF4444]/10 text-[#52525B] hover:text-[#EF4444]"><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>
      <div>
        <p className="text-[14px] font-semibold text-[#EDEDED] mb-1">{course.title}</p>
        {course.description && <p className="text-[11px] text-[#71717A] line-clamp-2">{course.description}</p>}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ background: cat.color + "20", color: cat.color }}>{cat.label}</span>
        <span className="text-[10px] text-[#52525B]">{course.total_lessons} lessons</span>
        {pct === 100 && <Trophy className="w-3 h-3 text-[#00C781] ml-auto" />}
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] text-[#52525B]">{course.completed_lessons}/{course.total_lessons}</span>
          <span className="text-[9px] font-semibold" style={{ color: course.color }}>{pct}%</span>
        </div>
        <div className="h-1 rounded-full overflow-hidden bg-[#1A1A1A]">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: course.color }} />
        </div>
      </div>
    </div>
  );
}

// ─── Main Courses Page ────────────────────────────────────────────────────────
export default function Courses() {
  const { current } = useWorkspace();
  const wsId = current?.workspace_id;
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCourse, setActiveCourse] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    if (!wsId) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/courses`);
      setCourses(data || []);
    } catch {} finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { load(); }, [wsId]);

  const createCourse = async (body) => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/courses`, body);
      setCourses(c => [data, ...c]);
      setShowModal(false);
      setActiveCourse(data);
    } catch { toast.error("Failed to create"); }
  };

  const updateCourse = async (body) => {
    try {
      const { data } = await api.put(`/workspaces/${wsId}/courses/${editingCourse.course_id}`, body);
      setCourses(c => c.map(x => x.course_id === data.course_id ? data : x));
      if (activeCourse?.course_id === data.course_id) setActiveCourse(data);
      setEditingCourse(null);
    } catch {}
  };

  const deleteCourse = async (id) => {
    if (!window.confirm("Delete this course and all its content?")) return;
    try {
      await api.delete(`/workspaces/${wsId}/courses/${id}`);
      setCourses(c => c.filter(x => x.course_id !== id));
      if (activeCourse?.course_id === id) setActiveCourse(null);
    } catch {}
  };

  const refreshCourse = () => { load(); };

  const visible = filter === "all" ? courses : courses.filter(c => c.category === filter);

  if (activeCourse) {
    const fresh = courses.find(c => c.course_id === activeCourse.course_id) || activeCourse;
    return (
      <>
        <CourseDetail course={fresh} wsId={wsId} onBack={() => setActiveCourse(null)} onCourseUpdate={refreshCourse} />
        {editingCourse && <CourseModal initial={editingCourse} onClose={() => setEditingCourse(null)} onSave={updateCourse} />}
      </>
    );
  }

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      {/* Header */}
      <div className="px-8 py-5 border-b border-[#1A1A1A] flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-[18px] font-bold text-[#EDEDED]">Courses</h1>
          <p className="text-[11px] text-[#52525B]">Self-paced learning with linear, locked progression</p>
        </div>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-semibold text-white bg-[#5A90F5] hover:bg-[#4A80E5] transition-colors">
          <Plus className="w-3.5 h-3.5" /> New course
        </button>
      </div>

      {/* Category filter */}
      <div className="px-8 py-3 border-b border-[#1A1A1A] flex gap-2 flex-shrink-0">
        <button onClick={() => setFilter("all")}
          className={`px-3 py-1 rounded-full text-[10px] font-medium transition-all ${filter === "all" ? "bg-[#1A1A1A] text-[#EDEDED]" : "text-[#52525B] hover:text-[#A1A1AA]"}`}>
          All ({courses.length})
        </button>
        {CATEGORIES.map(cat => {
          const count = courses.filter(c => c.category === cat.id).length;
          if (count === 0) return null;
          return (
            <button key={cat.id} onClick={() => setFilter(cat.id)}
              className="px-3 py-1 rounded-full text-[10px] font-medium transition-all"
              style={{ background: filter === cat.id ? cat.color + "25" : "transparent", color: filter === cat.id ? cat.color : "#52525B" }}>
              {cat.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto p-8">
        {loading ? (
          <div className="text-center py-16 text-[#52525B] text-[12px]">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#111] border border-[#1A1A1A] flex items-center justify-center">
              <BookOpen className="w-7 h-7 text-[#2A2A2A]" />
            </div>
            <div className="text-center">
              <p className="text-[15px] font-semibold text-[#52525B]">No courses yet</p>
              <p className="text-[11px] text-[#3A3A3A] mt-1">Create your first course to get started</p>
            </div>
            <button onClick={() => setShowModal(true)}
              className="px-5 py-2.5 rounded-xl text-[12px] font-semibold text-white bg-[#5A90F5] hover:bg-[#4A80E5] transition-colors">
              Create first course
            </button>
          </div>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
            {visible.map(c => (
              <CourseCard key={c.course_id} course={c}
                onOpen={setActiveCourse}
                onEdit={setEditingCourse}
                onDelete={deleteCourse} />
            ))}
          </div>
        )}
      </div>

      {showModal && <CourseModal onClose={() => setShowModal(false)} onSave={createCourse} />}
      {editingCourse && <CourseModal initial={editingCourse} onClose={() => setEditingCourse(null)} onSave={updateCourse} />}
    </div>
  );
}
